import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronRight, ChevronDown, ChevronsUpDown } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatMonto, formatFecha, hoyCaracas } from '../utils/format';
import { Avisos, descargarExcel, claseMonto } from '../components/contabilidad/ReporteFinanciero';
import SelectorMoneda, { useVistaMoneda } from '../components/reportes/SelectorMoneda';
import Exportar from '../components/reportes/Exportar';
import EncabezadoImpresion from '../components/reportes/EncabezadoImpresion';
import BotonAyuda from '../components/BotonAyuda';
import { filaClickeable, FOCO_FILA } from '../utils/filaClickeable';

// Estado de resultados — #110 (fase 1, base caja, sobre las categorías
// clasificadas en Cuentas Contables). El cálculo vive en el backend
// (estadosFinancierosController); esta pantalla solo arma la tabla.
// Cada partida sale una sola vez, en el bloque de su clasificación, con
// Ingreso / Egreso / Total (neto con signo).

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const etiquetaMes = (p) => `${MESES[Number(p.slice(5, 7)) - 1]} ${p.slice(0, 4)}`;

// Orden de lectura: cada utilidad va después de los bloques que la forman.
const ESTRUCTURA = [
  { seccion: 'ingreso_operativo' },
  { seccion: 'costo' },
  { resultado: 'utilidad_bruta', label: 'Utilidad bruta', requiere: ['ingreso_operativo', 'costo'] },
  { seccion: 'gasto' },
  { resultado: 'utilidad_operativa', label: 'Utilidad operativa', requiere: ['ingreso_operativo', 'costo', 'gasto'] },
  { seccion: 'otro_ingreso' },
  { seccion: 'otro_gasto' },
  { seccion: 'sin_clasificar' },
  { resultado: 'utilidad_neta', label: 'Utilidad neta', final: true }
];

// Agrupa las líneas de un bloque por partida padre, con su subtotal.
function agruparLineas(lineas, periodos) {
  const grupos = [];
  const porId = new Map();
  lineas.forEach((l) => {
    if (!porId.has(l.grupo_id)) {
      const g = { id: l.grupo_id, nombre: l.grupo_nombre, lineas: [] };
      porId.set(l.grupo_id, g);
      grupos.push(g);
    }
    porId.get(l.grupo_id).lineas.push(l);
  });
  grupos.forEach((g) => {
    const suma = (k) => g.lineas.reduce((acc, l) => acc + l[k], 0);
    g.ingreso = suma('ingreso');
    g.egreso = suma('egreso');
    g.total = suma('total');
    g.periodos = Object.fromEntries(periodos.map((p) => [p, g.lineas.reduce((acc, l) => acc + (l.periodos[p] || 0), 0)]));
  });
  return grupos;
}

// Monto o raya si es cero: con muchas partidas los ceros llenan la tabla de ruido.
function Monto({ valor, color = '' }) {
  if (!valor || Math.abs(valor) < 0.005) return <span className="text-slate-300">—</span>;
  return <span className={color || claseMonto(valor)}>{formatMonto(valor)}</span>;
}

function Tarjeta({ titulo, valor, color, detalle }) {
  return (
    <div className="bg-white rounded-xl shadow-sm p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{titulo}</p>
      <p className={`mt-1 text-2xl font-bold tabular-nums ${color}`}>{formatMonto(valor)}</p>
      {detalle && <p className="mt-0.5 text-xs text-slate-400">{detalle}</p>}
    </div>
  );
}

export default function EstadoResultados() {
  const { empresaActiva, empresas } = useAuth();
  const empresaNombre = empresas.find((e) => String(e.empresa_id) === String(empresaActiva))?.empresa_nombre;

  // ?desde=&hasta= (enlace desde el Cierre del período, #121).
  const [params] = useSearchParams();
  const dia = (k) => (/^\d{4}-\d{2}-\d{2}$/.test(params.get(k) || '') ? params.get(k) : null);
  const [desde, setDesde] = useState(() => dia('desde') || `${hoyCaracas().slice(0, 4)}-01-01`);
  const [hasta, setHasta] = useState(() => dia('hasta') || hoyCaracas());
  const [vista, setVista] = useVistaMoneda();
  const [porMes, setPorMes] = useState(false);
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [abiertos, setAbiertos] = useState({});

  useEffect(() => {
    if (!empresaActiva || !desde || !hasta) return;
    setCargando(true);
    setError('');
    api
      .get('/reportes/estado-resultados', { params: { desde, hasta, vista } })
      .then(({ data }) => setDatos(data))
      .catch((err) => setError(err.response?.data?.error || 'No se pudo cargar el estado de resultados'))
      .finally(() => setCargando(false));
  }, [empresaActiva, desde, hasta, vista]);

  async function exportar() {
    await descargarExcel(
      '/reportes/estado-resultados/excel',
      { desde, hasta, vista, agrupar: porMes ? 'mes' : 'total' },
      `estado-resultados${empresaNombre ? `-${empresaNombre}` : ''}-${desde}-${hasta}.xlsx`
    );
  }

  const periodos = porMes && datos ? datos.periodos : [];
  const columnas = periodos.length + 4;
  const alternar = (clave) => setAbiertos((prev) => ({ ...prev, [clave]: !prev[clave] }));

  // Todas las partidas padre con sub-partidas, para expandir/contraer de una vez.
  const clavesGrupos = datos
    ? datos.secciones.flatMap((s) => agruparLineas(s.lineas, []).filter((g) => !(g.lineas.length === 1 && g.lineas[0].es_padre)).map((g) => `${s.key}|${g.id}`))
    : [];
  const todoAbierto = clavesGrupos.length > 0 && clavesGrupos.every((k) => abiertos[k]);
  const alternarTodo = () => setAbiertos(todoAbierto ? {} : Object.fromEntries(clavesGrupos.map((k) => [k, true])));

  // Celdas numéricas de una fila: meses (neto), ingreso, egreso, total.
  const celdas = (obj, { fuerte = false } = {}) => (
    <>
      {periodos.map((p) => (
        <td key={p} className="px-3 py-2 text-right whitespace-nowrap tabular-nums"><Monto valor={obj.periodos[p]} /></td>
      ))}
      <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums"><Monto valor={obj.ingreso} color="text-green-700" /></td>
      <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums"><Monto valor={obj.egreso} color="text-red-600" /></td>
      <td className={`px-3 py-2 text-right whitespace-nowrap tabular-nums ${fuerte ? 'font-semibold' : 'font-medium'}`}><Monto valor={obj.total} /></td>
    </>
  );

  function filasSeccion(s) {
    if (s.lineas.length === 0) return null;
    return (
      <React.Fragment key={s.key}>
        {/* Cabecera del bloque con su total: evita una fila extra de "Total …" */}
        <tr className="border-t-2 border-slate-200 bg-slate-50 text-slate-700">
          <td className="px-3 py-2 text-xs font-semibold uppercase tracking-wide">{s.label}</td>
          {celdas(s, { fuerte: true })}
        </tr>
        {agruparLineas(s.lineas, periodos).map((g) => {
          const clave = `${s.key}|${g.id}`;
          const soloPadre = g.lineas.length === 1 && g.lineas[0].es_padre;
          if (soloPadre) {
            return (
              <tr key={clave} className="border-t border-slate-100 hover:bg-slate-50 transition-colors">
                <td className="px-3 py-2 text-slate-700" style={{ paddingLeft: 34 }}>{g.nombre}</td>
                {celdas(g.lineas[0])}
              </tr>
            );
          }
          const abierto = !!abiertos[clave];
          return (
            <React.Fragment key={clave}>
              <tr {...filaClickeable(() => alternar(clave))} className={`border-t border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer ${FOCO_FILA}`}>
                <td className="px-3 py-2 text-slate-700" style={{ paddingLeft: 14 }}>
                  <span className="inline-flex items-center gap-1">
                    <span className="print:hidden">
                      {abierto ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
                    </span>
                    {g.nombre}
                    <span className="text-xs text-slate-400">({g.lineas.length})</span>
                  </span>
                </td>
                {celdas(g)}
              </tr>
              {abierto && g.lineas.map((l) => (
                <tr key={l.clave} className="border-t border-slate-50 text-slate-500 text-[13px]">
                  <td className="px-3 py-1.5" style={{ paddingLeft: 54 }}>{l.es_padre ? `${l.nombre} (general)` : l.nombre}</td>
                  {celdas(l)}
                </tr>
              ))}
            </React.Fragment>
          );
        })}
      </React.Fragment>
    );
  }

  const avisos = [];
  if (datos) {
    const a = datos.avisos;
    if (a.movimientos_sin_clasificar) {
      avisos.push({
        texto: `${a.movimientos_sin_clasificar} movimientos son de partidas sin clasificar: salen en el bloque "Sin clasificar".`,
        enlace: { to: '/categorias', label: 'Clasificar en Cuentas Contables' }
      });
    }
    if (a.movimientos_sin_categoria) {
      avisos.push({ texto: `${a.movimientos_sin_categoria} movimientos no tienen categoría: salen como "Sin categoría" dentro de "Sin clasificar".` });
    }
    if (a.movimientos_tasa_aproximada) {
      avisos.push({ texto: `${a.movimientos_tasa_aproximada} movimientos en Bs se convirtieron con una tasa aproximada (no hay tasa ${vista === 'USD' ? 'USD genérica' : 'BCV'} cargada para su día).` });
    }
    if (a.movimientos_sin_tasa) {
      avisos.push({ texto: `${a.movimientos_sin_tasa} movimientos en Bs no se pudieron convertir (no hay ninguna tasa para su fecha) y quedaron en 0.` });
    }
  }

  const neta = datos?.utilidad_neta;
  const margen = neta && neta.ingreso ? (neta.total / neta.ingreso) * 100 : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between print:hidden">
        <h1 className="text-2xl font-bold text-slate-800">
          Estado de resultados
          {empresaNombre && <span className="text-slate-400 font-normal"> · {empresaNombre}</span>}
          <BotonAyuda />
        </h1>
        <div className="flex items-center gap-2 flex-wrap">
          <SelectorMoneda vista={vista} onChange={setVista} />
          <Exportar onExcel={exportar} disabled={!datos} />
        </div>
      </div>

      <EncabezadoImpresion titulo="Estado de resultados" detalle={`Del ${formatFecha(desde)} al ${formatFecha(hasta)} · base caja · montos en ${vista === 'BCV' ? 'USD a tasa BCV' : 'USD'}`} />

      <div className="flex flex-wrap items-end gap-3 print:hidden">
        <div>
          <label className="block text-xs text-slate-500 mb-1">Desde</label>
          <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="border rounded-md px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Hasta</label>
          <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="border rounded-md px-3 py-2 text-sm" />
        </div>
        <div className="inline-flex rounded-md border overflow-hidden text-sm">
          {[[false, 'Total del período'], [true, 'Por mes']].map(([v, label]) => (
            <button
              key={label}
              type="button"
              onClick={() => setPorMes(v)}
              className={`px-3 py-2 whitespace-nowrap ${porMes === v ? 'bg-primary-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
            >
              {label}
            </button>
          ))}
        </div>
        {clavesGrupos.length > 0 && (
          <button
            type="button"
            onClick={alternarTodo}
            title={todoAbierto ? 'Contraer todas las partidas' : 'Expandir todas las partidas'}
            className="p-2 rounded-md border text-slate-500 hover:bg-slate-50"
          >
            <ChevronsUpDown size={18} />
          </button>
        )}
      </div>

      <Avisos avisos={avisos} />

      {datos && !cargando && !error && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Tarjeta titulo="Ingresos" valor={neta.ingreso} color="text-green-700" />
          <Tarjeta titulo="Egresos" valor={neta.egreso} color="text-red-600" />
          <Tarjeta
            titulo="Utilidad neta"
            valor={neta.total}
            color={neta.total < 0 ? 'text-red-600' : 'text-primary-700'}
            detalle={margen !== null ? `Margen ${formatMonto(margen)} % sobre ingresos` : null}
          />
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {error ? (
          <p className="p-6 text-center text-red-600 text-sm">{error}</p>
        ) : cargando || !datos ? (
          <p className="p-6 text-center text-slate-400 text-sm">Cargando…</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-left">
                <tr>
                  <th className="px-3 py-2.5">Partida</th>
                  {periodos.map((p) => (
                    <th key={p} className="px-3 py-2.5 text-right whitespace-nowrap capitalize">{etiquetaMes(p)}</th>
                  ))}
                  <th className="px-3 py-2.5 text-right">Ingreso</th>
                  <th className="px-3 py-2.5 text-right">Egreso</th>
                  <th className="px-3 py-2.5 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {ESTRUCTURA.map((item) => {
                  if (item.seccion) return filasSeccion(datos.secciones.find((s) => s.key === item.seccion));
                  // Una utilidad intermedia sin bloques clasificados detrás solo mostraría rayas.
                  if (item.requiere && item.requiere.every((k) => datos.secciones.find((s) => s.key === k).lineas.length === 0)) return null;
                  const r = datos[item.resultado];
                  return (
                    <tr
                      key={item.resultado}
                      className={`border-t-2 border-slate-300 font-semibold text-slate-800 ${item.final ? 'bg-primary-50 text-base' : 'bg-white'}`}
                    >
                      <td className="px-3 py-2.5 uppercase tracking-wide text-xs">{item.label}</td>
                      {celdas(r, { fuerte: true })}
                    </tr>
                  );
                })}
                {datos.secciones.every((s) => s.lineas.length === 0) && (
                  <tr>
                    <td colSpan={columnas} className="p-6 text-center text-slate-400">No hay movimientos en ese rango.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {datos && datos.fuera_del_resultado.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-400 bg-slate-50">
            Fuera del estado de resultados (van al balance o no afectan)
          </div>
          <table className="w-full text-sm">
            <tbody>
              {datos.fuera_del_resultado.map((f) => (
                <tr key={f.clase} className="border-t">
                  <td className="px-3 py-2 text-slate-600">{f.label}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums"><Monto valor={f.total} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="px-3 py-2 text-xs text-slate-400 border-t">Positivo = entró más de lo que salió en el período.</p>
        </div>
      )}
    </div>
  );
}
